import React, { useState, useEffect } from "react";
import "./BMIQuiz.css";
import Header from "./BMIQuizHeader";
import Footer from "./Footer";
import {
  fetchQuestions,
  submitFormData,
  fetchClinics,
} from "../../redux/actions/bmiQuizActions";
import { useDispatch, useSelector } from "react-redux";

const BMIQuizScreen = () => {
  const dispatch = useDispatch();
  const { questions, error, successMessage, isSubmitted } = useSelector(
    (state) => state.bmiQuiz
  );
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    month: "",
    day: "",
    year: "",
    clinicName: "",
    heightFeet: "",
    heightInches: "",
    height: "",
    weight: "",
    email: "",
    phoneNumber: "",
    authorization: false,
    acceptTerms: false,
  });

  const { clinics } = useSelector((state) => state.bmiQuiz);
  const welcomeQuestion = questions.find((q) => q.name === "bmiquiz_welcome");
  const authText = questions.find((q) => q.name === "bmiquiz_auth");
  const futureDate = new Date();
  futureDate.setFullYear(futureDate.getFullYear() + 1);
  const regularQuestions = questions.filter(
    (q) => !["bmiquiz_welcome", "bmiquiz_auth"].includes(q.name)
  );

  const formattedDate = futureDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    return new Date(year, month, 0).getDate();
  };

  const handleDropdownChange = (field, value) => {
    const updatedFormData = { ...formData, [field]: value };

    if (field === "month" || field === "year") {
      updatedFormData.day = Math.min(
        updatedFormData.day || 1,
        getDaysInMonth(updatedFormData.month, updatedFormData.year)
      );
    }
    if (updatedFormData.month && updatedFormData.day && updatedFormData.year) {
      updatedFormData.dateOfBirth = `${String(updatedFormData.month).padStart(
        2,
        "0"
      )}/${String(updatedFormData.day).padStart(2, "0")}/${
        updatedFormData.year
      }`;
    }

    setFormData(updatedFormData);
  };

  useEffect(() => {
    const loadData = async () => {
      await dispatch(fetchQuestions());
      await dispatch(fetchClinics());
    };
    loadData();
  }, [dispatch]);

  if (error) {
    return <p>Error: {error}</p>;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    })); 
  };

  const validateInputs = () => {
    const currentQuestion = regularQuestions[step - 1];
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;

    if (step === questions.length + 1) {
      if (!formData.authorization)
        newErrors.authorization =
          "you must agree to the authorization to continue";
    } else if (currentQuestion?.name === "bmiquiz_1") {
      if (!formData.firstName) newErrors.firstName = "First name is required";
      if (!formData.lastName) newErrors.lastName = "Last name is required";
    } else if (currentQuestion?.answerType === "checkbox") {
      if (!formData.authorization) newErrors.authorization = true;
      if (!formData.acceptTerms) newErrors.acceptTerms = true; 
    } else if (currentQuestion?.answerType === "select") {
      if (!formData.clinicName) {
        newErrors.clinicName = "Please select a clinic";
      } else if (
        formData.clinicName ===
          clinics.find((clinic) => clinic.clinicName === "Other")?.id &&
        !formData.otherClinicName
      ) {
        newErrors.otherClinicName = "Please specify your clinic name";
      }
    } else if (currentQuestion?.name === "bmiquiz_2") {
      if (!formData.dateOfBirth)
        newErrors.dateOfBirth = "Please select a valid birth date";
    } else if (currentQuestion?.name === "bmiquiz_4") {
      if (!formData.heightFeet) newErrors.heightFeet = "Please select feet";
      if (!formData.heightInches)
        newErrors.heightInches = "Please select inches";
    } else if (currentQuestion?.name === "bmiquiz_5") {
      if (!formData.weight) newErrors.weight = "Weight is required";
    } else if (currentQuestion?.name === "bmiquiz_6") {
      if (!formData.email) {
        newErrors.email = "Email address is required";
      } else if (!emailRegex.test(formData.email)) {
        newErrors.email =
          "Please enter a valid email address (e.g., example@domain.com)";
      }
    } else if (currentQuestion?.name === "bmiquiz_7") {
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = "Phone number is required";
      } else if (!phoneRegex.test(formData.phoneNumber)) {
        newErrors.phoneNumber =
          "Please enter a valid phone number (e.g., 123-456-7890)";
      }
    } else if (!formData.acceptTerms) {
      newErrors.acceptTerms = "You must agree to the above statements to continue.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    dispatch(submitFormData(formData));
    console.log("data", formData, clinics);
  };

  const nextStep = () => {
    if (step === 0) {
      setStep(1);
      return;
    }

    if (!validateInputs()) return;

    setStep((prevStep) => {
      const nextStep = prevStep + 1;

      if (nextStep === questions.length) {
        if (!formData.authorization) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            authorization: "You must agree to the authorization to continue",
          }));
          return prevStep;
        }

        handleSubmit();
        return prevStep;
      }

      return nextStep;
    });
  };

  return (
    <div className="quiz-container">
      <Header />
      <div className="card">
        {step === 0 ? (
          <div className="welcome-card">
            <p>{welcomeQuestion?.questionText || "Loading..."}</p>
            <button
              onClick={nextStep}
              className="btn"
              disabled={!welcomeQuestion}
            >
              Start
            </button>
          </div>
        ) : step <= regularQuestions.length ? (
          <div className="question-card">
            {regularQuestions[step - 1]?.answerType == "Note" ? (
              <h3 style={{textAlign:'justify'}} dangerouslySetInnerHTML={{
               __html: clinics.find((u) => u.id === formData.clinicName)
                  ?.clinicName === "Other"
                  ? regularQuestions[step - 1]?.questionText.replace(
                      "#clinic#",
                      formData.otherClinicName
                    )
                  : regularQuestions[step - 1]?.questionText.replace(
                      "#clinic#",
                      clinics.find((u) => u.id === formData.clinicName)
                        ?.clinicName
                    )}}
              />
            ) : (
              <h3>{regularQuestions[step - 1]?.questionText}</h3>
            )}

            <div className="input-container">
              {regularQuestions[step - 1]?.answerType === "select" ? (
                <>
                  <select
                    name="clinicName"
                    value={formData.clinicName}
                    onChange={handleChange}
                    className={`input-field${
                      errors.clinicName ? " error" : ""
                    }`}
                    required
                  >
                    <option value="" disabled>
                      Select an option
                    </option>
                    {clinics
                      .filter((clinic) => !clinic.isDeleted) // Exclude deleted clinics
                      .map((clinic) => (
                        <option key={clinic.id} value={clinic.id}>
                          {clinic.clinicName}
                        </option>
                      ))}
                  </select>
                  {errors.clinicName && (
                    <span className="error-message">{errors.clinicName}</span>
                  )}

                  {/* Check if selected clinic is "Other" */}
                  {formData.clinicName ===
                    clinics.find((clinic) => clinic.clinicName === "Other")
                      ?.id && (
                    <>
                      <input
                        type="text"
                        name="otherClinicName"
                        value={formData.otherClinicName || ""}
                        onChange={handleChange}
                        placeholder="Please specify your clinic"
                        className={`input-field${
                          errors.otherClinicName ? " error" : ""
                        }`}
                        style={{ marginTop: "10px" }}
                      />
                      {errors.otherClinicName && (
                        <span className="error-message">
                          {errors.otherClinicName}
                        </span>
                      )}
                    </>
                  )}
                </>
              ) : regularQuestions[step - 1]?.name === "bmiquiz_1" ? (
                <>
                  <input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`input-field${errors.firstName ? " error" : ""}`}
                    placeholder="First Name"
                    required
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                  <input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`input-field${errors.lastName ? " error" : ""}`}
                    placeholder="Last Name"
                    required
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </>
              ) : regularQuestions[step - 1]?.name === "bmiquiz_2" ? (
                <>
                  <div className="date-dropdowns">
                    {/* Month Dropdown */}
                    <select
                      value={formData.month || ""}
                      onChange={(e) =>
                        handleDropdownChange("month", e.target.value)
                      }
                      className={`input-field${
                        errors.dateOfBirth ? " error" : ""
                      }`}
                    >
                      <option value="" disabled>
                        Month
                      </option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>

                    {/* Day Dropdown */}
                    <select
                      value={formData.day || ""}
                      onChange={(e) =>
                        handleDropdownChange("day", e.target.value)
                      }
                      className={`input-field${
                        errors.dateOfBirth ? " error" : ""
                      }`}
                    >
                      <option value="" disabled>
                        Day
                      </option>
                      {Array.from(
                        {
                          length: getDaysInMonth(formData.month, formData.year),
                        },
                        (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        )
                      )}
                    </select>

                    {/* Year Dropdown */}
                    <select
                      value={formData.year || ""}
                      onChange={(e) =>
                        handleDropdownChange("year", e.target.value)
                      }
                      className={`input-field${
                        errors.dateOfBirth ? " error" : ""
                      }`}
                    >
                      <option value="" disabled>
                        Year
                      </option>
                      {Array.from({ length: 76 }, (_, i) => (
                        <option key={2000 - i} value={2000 - i}>
                          {2000 - i}
                        </option>
                      ))}
                    </select>
                  </div>

                  {errors.dateOfBirth && (
                    <span className="error-message">{errors.dateOfBirth}</span>
                  )}
                </>
              ) : regularQuestions[step - 1]?.name === "bmiquiz_4" ? (
                <div className="height-inputs">
                  <div className="height-group">
                    <label htmlFor="heightFeet" className="height-label">
                      Feet:
                    </label>
                    <select
                      id="heightFeet"
                      name="heightFeet"
                      value={formData.heightFeet || ""}
                      onChange={handleChange}
                      className={`input-field${
                        errors.heightFeet ? " error" : ""
                      }`}
                      required
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {[...Array(8).keys()].slice(1).map((feet) => (
                        <option key={feet} value={feet}>
                          {feet}
                        </option>
                      ))}
                    </select>
                    {errors.heightFeet && (
                      <span className="error-message">{errors.heightFeet}</span>
                    )}
                  </div>

                  <div className="height-group">
                    <label htmlFor="heightInches" className="height-label">
                      Inches:
                    </label>
                    <select
                      id="heightInches"
                      name="heightInches"
                      value={formData.heightInches || ""}
                      onChange={handleChange}
                      className={`input-field${
                        errors.heightInches ? " error" : ""
                      }`}
                      required
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {[...Array(12).keys()].map((inch) => (
                        <option key={inch} value={inch}>
                          {inch}
                        </option>
                      ))}
                    </select>
                    {errors.heightInches && (
                      <span className="error-message">
                        {errors.heightInches}
                      </span>
                    )}
                  </div>
                </div>
              ) : regularQuestions[step - 1]?.name === "bmiquiz_5" ? (
                <>
                  <div className="weight-input">
                    <input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      className={`input-field${errors.weight ? " error" : ""}`}
                      required
                    />
                    <span className="weight-unit">lbs</span>
                  </div>
                  {errors.weight && (
                    <span className="error-message">{errors.weight}</span>
                  )}
                </>
              ) : regularQuestions[step - 1]?.name === "bmiquiz_6" ? (
                <>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input-field${errors.email ? " error" : ""}`}
                    required
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </>
              ) : regularQuestions[step - 1]?.name === "bmiquiz_7" ? (
                <>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className={`input-field${
                      errors.phoneNumber ? " error" : ""
                    }`}
                    required
                  />
                  {errors.phoneNumber && (
                    <span className="error-message">{errors.phoneNumber}</span>
                  )}
                </>
              ) : regularQuestions[step - 1]?.name === "bmiquiz_9" ? (
                <>
                  <div className="input-container authorization-container"> 
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="acceptTerms"
                        checked={formData.acceptTerms || false}
                        onChange={handleChange}
                        className="checkbox-input"
                        required
                      />
                      <span className="checkmark"></span>
                      <div className="authorization-text">
                        <p>I agree with all the above statements.</p>
                      </div>
                    </label>
                    {errors.acceptTerms && (
                      <div className="error-message">
                        {errors.acceptTerms}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>

            <button onClick={nextStep} className="btn">
              Next
            </button>
          </div>
        ) : isSubmitted ? (
          // Success message
          <div className="success-message">
            <div className="tick-icon"></div>
            <span>{successMessage}</span>
          </div>
        ) : (
          <div className="question-card authorization-card">
            <h2 className="authorization-title">Authorization</h2>
            <div className="input-container authorization-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="authorization"
                  checked={formData.authorization || false}
                  onChange={handleChange}
                  className="checkbox-input"
                  required
                />
                <span className="checkmark"></span>
                <div className="authorization-text">
                  <p>
                    {authText?.questionText
                      ? `${authText.questionText} ${formattedDate}`
                      : "Loading..."}
                  </p>
                </div>
              </label>
              {errors.authorization && (
                <div className="error-message">{errors.authorization}</div>
              )}
            </div>
            <button onClick={nextStep} className="btn">
              Submit
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default BMIQuizScreen;
